import React, { useCallback, useEffect, useState } from 'react';
import Layout, { NAVBAR_ISMINIMIZED_KEY } from '../../components/Layout/Layout.component';
import { SelectableTableColumn } from '../../components/SelectableTable/SelectableTable.types';
import { SelectableTable } from '../../components/SelectableTable/SelectableTable.component';
import { TableItem } from './RolesAndPermissions.types';
import { useStyles } from '../styles';
import { Typography } from '@material-ui/core';
import Breadcrumbs from '../../components/Breadcrumbs.component';
import { ButtonOne } from '../../components/ButtonOne/ButtonOne.component';
import Pagination from '../../components/Pagination.component';
import { AxiosResponse, AxiosError } from 'axios';
import { getRolesAndPermissions, deleteRole } from '../../services/rolesAndPermissions/rolesAndPermissions.service';
import {
  IRole,
  IRoleAndPermissions,
  IRoleAndPermissionsResponse,
  IRoleDeleteResult,
  RolesDeleteStatus,
} from '../../services/rolesAndPermissions/rolesAndPermissions.types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TooltipOne from '../../components/TooltipOne/TooltipOne.component';
import ActionsMenu from '../../components/ActionsMenu/ActionsMenu.component';
import { PermissionsForRole } from '../../components/Roles/PermissionsForRole/PermissionsForRole.component';
import { AddRole } from '../../components/Roles/AddRole/AddRole.component';
import { defaultPageInfo, IPageInfo } from '../../models/page.types';
import usePermission, { UserPermission } from '../../hooks/usePermission';
import { ActionMenuItem } from '../../components/ActionsMenu/ActionsMenu.types';
import { Prompt } from '../../components/Prompt/Prompt.component';
import { useLayoutContext } from '../../Context/Layout.context';
import { EditRole } from '../../components/Roles/EditRole/EditRole.component';
import useDocumentTitle from '../../hooks/DocumentTitle/documentTitle';
import { useTranslation } from 'react-i18next';
import { Paths } from '../../utils/routerUrls';

const RolesAndPermissions: React.FC = () => {
  const [rolesAndPermissions, setRoleWithPermissions] = useState<IRoleAndPermissions[]>([]);
  const [data, setData] = useState<TableItem[]>([]);
  const [sumOfPermissions, setSumOfPermissions] = useState<number>();
  const [sumOfRoles, setSumOfRoles] = useState<number>();
  const [rolePermissionManagementId, setRolePermissionManagementId] = useState<string | undefined>(undefined);
  const [isAddRoleOpen, setIsAddRoleOpen] = useState<boolean>(false);
  const [pageInfo, setPageInfo] = useState<IPageInfo>(defaultPageInfo);
  const classes = useStyles();

  const [deletingRolePromptData, setDeletingRolePromptData] = useState<
    { roleId: string; roleName: string } | undefined
  >(undefined);

  const [roleDataToEdit, setRoleDataToEdit] = useState<IRole | undefined>(undefined);

  const hasAdminRoleManagePermission = usePermission(UserPermission.AdminRoleManage);
  const { openAlert } = useLayoutContext();
  const { t } = useTranslation();
  useDocumentTitle(t('global:sidebar.roles'));

  // Check defult role and add tooltip
  const checkIfIsDefault = (roleName: string, isDefault: boolean) => {
    if (isDefault) {
      return (
        <p style={{ display: 'inline-flex' }}>
          <span style={{ marginRight: '5px', fontWeight: '700', paddingTop: '3px' }}>{roleName}</span>
          <TooltipOne title="Rola domyślna">
            <InfoOutlinedIcon fontSize="small" />
          </TooltipOne>
        </p>
      );
    } else {
      return <p style={{ fontWeight: '700' }}>{roleName}</p>;
    }
  };

  const getPages = useCallback(
    (_page?: number, _newPageSize?: number) => {
      setPageInfo((state) => ({
        ...state,
        currentPage: _page ? _page : 0,
        pageSize: _newPageSize ? _newPageSize : state.pageSize,
      }));
    },
    [pageInfo],
  );

  const handleCurrentPage = (response: IPageInfo) => {
    const { totalPages, pageSize, totalCount, hasPrevious, hasNext } = response;
    if (totalCount < pageInfo.pageSize) {
      setPageInfo({ currentPage: 0, totalPages, pageSize, totalCount, hasPrevious, hasNext });
    } else {
      setPageInfo(response);
    }
  };

  const refreshData = () => {
    getRolesAndPermissions(pageInfo.currentPage, pageInfo.pageSize)
      .then((response: AxiosResponse<IRoleAndPermissionsResponse>) => {
        setRoleWithPermissions(response.data.list);
        setSumOfPermissions(response.data.totalClaimsCount);
        handleCurrentPage(response.data.pageInfo);
        setSumOfRoles(response.data.pageInfo.totalCount);
      })
      .catch(() => {
        console.error('error');
      });
  };

  useEffect(() => {
    refreshData();
  }, [pageInfo.currentPage, pageInfo.pageSize]);

  const getActionsMenuIcon = (roleData: IRoleAndPermissions, rowIndex: number): JSX.Element => {
    const items: ActionMenuItem[] = [];
    if (hasAdminRoleManagePermission) {
      items.push(
        {
          text: 'Edycja roli',
          onClick: () =>
            setRoleDataToEdit({
              roleId: roleData.roleId,
              roleName: roleData.roleName,
              roleDescription: roleData.roleDescription,
            }),
        },
        {
          text: 'Zarządzaj uprawnieniami',
          onClick: () => setRolePermissionManagementId(roleData.roleId),
        },
      );

      if (!roleData.isDefault) {
        items.push({
          text: 'Usuń rolę',
          onClick: () => {
            setDeletingRolePromptData({ roleId: roleData.roleId, roleName: roleData.roleName });
          },
        });
      }
    }
    return <ActionsMenu menuItems={items} rowIndex={rowIndex} />;
  };

  // convert data for the tables
  useEffect(() => {
    const tableData: TableItem[] = rolesAndPermissions.map((data: any, index) => ({
      id: data.roleId,
      name: checkIfIsDefault(data.roleName, data.isDefault),
      description: data.roleDescription,
      permissions: data.claimsWithThatRoleCount,
      assignments: data.usersWithThatRoleCount,
      actions: getActionsMenuIcon(data, index),
    }));
    setData(tableData);
  }, [rolesAndPermissions]);

  const columns: SelectableTableColumn[] = [
    {
      id: 'name',
      type: 'text',
      title: t('roles:table.name'),
      filterType: null,
    },
    {
      id: 'description',
      type: 'text',
      title: t('roles:table.roleDescription'),
      filterType: null,
    },
    {
      id: 'permissions',
      type: 'text',
      title: t('roles:table.permissions'),
      filterType: null,
    },
    {
      id: 'assignments',
      type: 'text',
      title: t('roles:table.assignments'),
      filterType: null,
    },
    {
      id: 'actions',
      type: 'actions',
      title: 'Akcje',
      filterType: null,
      skipEnterMethod: true,
    },
  ];

  const handleDeleteRole = (roleId: string) => {
    deleteRole(roleId)
      .then(
        () => {
          openAlert('success', 'Usunięcie roli zakończone sukcesem');
          refreshData();
        },
        (error: AxiosError<IRoleDeleteResult>) => {
          openAlert(
            'error',
            error.response?.data.error === RolesDeleteStatus.UsersExistsInRole
              ? `Usunięcie roli: ${deletingRolePromptData?.roleName} nie powiodło się ponieważ są przypisani do niej użytkownicy systemu ${t('global:shortNames.system')}`
              : `Usunięcie roli: ${deletingRolePromptData?.roleName} nie powiodło się. Spróbuj ponownie później.`,
          );
        },
      )
      .finally(() => {
        setDeletingRolePromptData(undefined);
      });
  };

  const handleRolePermissionsUpdate = () => {
    setRolePermissionManagementId(undefined);
    refreshData();
  };
  return (
    <React.Fragment>
      <Layout viewType={'admin'}>
        <div className={classes.tableAndBreadcrumContainer}>
          <div className={classes.breadcrumbHeader}>
            <Breadcrumbs listRoute={Paths.roles} currentList={t('global:sidebar.roles')} />
            <div className={classes.buttonsContainer}>
              <ButtonOne
                className={classes.primary}
                variant="contained"
                aria-label="Dodaj Rolę"
                onClick={() => setIsAddRoleOpen(true)}
              >
                <span style={{ margin: '0px 10px 0px -4px', fontSize: '20px' }}>+</span>{' '}
                {t('roles:header.addRoleButton')}
              </ButtonOne>
            </div>
          </div>
          <div className={classes.tableWhiteCardFlex}>
            <div className={classes.filtersContainerFlex}>
              <Typography variant={'h1'} className={classes.gridHeader}>
                {t('roles:header.title')}
              </Typography>
              <div className={classes.leftContainer}>
                <span className={classes.smallText}>
                  {t('roles:header.rolesSum')}: {sumOfRoles}
                </span>
                <span className={classes.smallText}>
                  {t('roles:header.permissionsSum')}: {sumOfPermissions}
                </span>
                <Pagination className={classes.paginationFlex} pageInfo={pageInfo} getPage={getPages} />
              </div>
            </div>
            <div className={`${classes.tableContainerFlex} ${classes.tableContainerFlexResponsive}`}>
              <SelectableTable<TableItem>
                columns={columns}
                rows={data}
                caption={t('global:sidebar.roles')}
              />
            </div>
          </div>
        </div>
      </Layout>
      {rolePermissionManagementId !== undefined && (
        <PermissionsForRole
          roleId={rolePermissionManagementId}
          onClose={() => setRolePermissionManagementId(undefined)}
          onUpdate={handleRolePermissionsUpdate}
        />
      )}
      {isAddRoleOpen && <AddRole onClose={() => setIsAddRoleOpen(false)} onChange={() => refreshData()} />}
      {roleDataToEdit && (
        <EditRole onClose={() => setRoleDataToEdit(undefined)} onChange={refreshData} roleProp={roleDataToEdit} />
      )}
      {deletingRolePromptData && (
        <Prompt
          type="confirmation"
          open={true}
          size="xs"
          onConfirm={() => handleDeleteRole(deletingRolePromptData.roleId)}
          onCancel={() => setDeletingRolePromptData(undefined)}
          mainActionButtonText="TAK"
          mainText="Czy na pewno chcesz usunąć rolę:"
          subTexts={[{ text: deletingRolePromptData.roleName }]}
        />
      )}
    </React.Fragment>
  );
};

export default RolesAndPermissions;
